import { getSupabaseClient } from '../utils/supabase/client';
import type { SoftwareBillingSummary, SoftwareLicenceAllowance, SoftwareLicencePreview } from '../shared-ui/membership/softwareLicenceModel';
import {
  SoftwareBillingError, type SoftwareFieldAccess, isRecord, parseSoftwareAccess, parseSoftwareConfirm, parseSoftwarePreview, parseSoftwareSummary, validateSoftwarePaymentUrl,
} from '../shared-ui/membership/softwareBillingParsing';

const BASE = 'https://sbuatwyiqtzloxzljnfa.supabase.co/functions/v1/software-billing';

export type { SoftwareLicenceAllowance, SoftwareBillingSummary, SoftwareLicencePreview };
export { SoftwareBillingError, validateSoftwarePaymentUrl };

export interface SoftwareCheckoutStatus {
  status: 'pending' | 'active' | 'expired' | 'manage_billing';
  workspaceId?: string;
  message?: string;
}

async function request(path: string, body?: Record<string, unknown>, signal?: AbortSignal): Promise<unknown> {
  const { data, error } = await getSupabaseClient().auth.getSession();
  if (error || !data.session?.access_token) throw new SoftwareBillingError('Please sign in to continue.', 'sign_in_required');
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  const timeout = window.setTimeout(abort, 30_000);
  try {
    const response = await fetch(`${BASE}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: { Authorization: `Bearer ${data.session.access_token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
      cache: 'no-store',
      signal: controller.signal,
    });
    const payload: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const code = isRecord(payload) && typeof payload.code === 'string' ? payload.code : null;
      const message = response.status === 401 ? 'Please sign in again to continue.'
        : response.status === 429 ? 'Please wait a moment before trying again.'
          : isRecord(payload) && typeof payload.error === 'string' ? payload.error
            : 'We could not complete that request. Please try again.';
      throw new SoftwareBillingError(message, code);
    }
    return payload;
  } catch (error: unknown) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    if (error instanceof SoftwareBillingError) throw error;
    throw new SoftwareBillingError(controller.signal.aborted
      ? 'This is taking longer than expected. Please try again.'
      : 'We could not connect. Check your connection and try again.');
  } finally {
    window.clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
  }
}

export const SoftwareBillingService = {
  async access(workspaceId: string, signal?: AbortSignal): Promise<SoftwareFieldAccess> {
    return parseSoftwareAccess(await request(`/access?workspaceId=${encodeURIComponent(workspaceId)}`, undefined, signal));
  },
  async cancelCheckout(intentId: string, signal?: AbortSignal): Promise<{ status: 'cancelled' } | { status: 'pending'; sessionId: string }> {
    const data = await request('/checkout/cancel', { intentId }, signal);
    if (isRecord(data) && data.status === 'cancelled') return { status: 'cancelled' };
    if (isRecord(data) && data.status === 'pending' && typeof data.sessionId === 'string') return { status: 'pending', sessionId: data.sessionId };
    throw new SoftwareBillingError('We could not close this checkout safely. Please try again before changing your team size.');
  },
  async portal(workspaceId: string, signal?: AbortSignal): Promise<string> {
    const data = await request('/portal', { workspaceId }, signal);
    return validateSoftwarePaymentUrl(isRecord(data) ? data.url : null);
  },
  async checkout(input: { quantity: number; requestId: string; companyName: string; ownerName: string }, signal?: AbortSignal): Promise<{ url: string; sessionId: string } | { status: 'active' | 'manage_billing'; workspaceId: string } | { status: 'pending'; sessionId: string }> {
    if (!Number.isSafeInteger(input.quantity) || input.quantity < 1 || input.quantity > 1000) throw new SoftwareBillingError('Choose between 1 and 1,000 full users.');
    const data = await request('/checkout', input, signal);
    if (isRecord(data) && (data.status === 'active' || data.status === 'manage_billing') && typeof data.workspaceId === 'string') return { status: data.status, workspaceId: data.workspaceId };
    if (isRecord(data) && data.status === 'pending' && typeof data.sessionId === 'string') return { status: 'pending', sessionId: data.sessionId };
    if (!isRecord(data) || typeof data.sessionId !== 'string') throw new SoftwareBillingError('We could not start checkout. Please try again.');
    return { url: validateSoftwarePaymentUrl(data.url), sessionId: data.sessionId };
  },
  async checkoutStatus(sessionId: string, signal?: AbortSignal): Promise<SoftwareCheckoutStatus> {
    const data = await request(`/checkout/status?sessionId=${encodeURIComponent(sessionId)}`, undefined, signal);
    if (!isRecord(data) || !['pending', 'active', 'expired', 'manage_billing'].includes(String(data.status))
      || (data.workspaceId !== undefined && typeof data.workspaceId !== 'string')
      || ((data.status === 'active' || data.status === 'manage_billing') && typeof data.workspaceId !== 'string')
      || (data.message !== undefined && typeof data.message !== 'string')) throw new SoftwareBillingError('We could not verify your checkout. Please try again.');
    return data as unknown as SoftwareCheckoutStatus;
  },
  async summary(workspaceId: string, signal?: AbortSignal): Promise<SoftwareBillingSummary | null> {
    return parseSoftwareSummary(await request(`/summary?workspaceId=${encodeURIComponent(workspaceId)}`, undefined, signal));
  },
  async preview(input: { workspaceId: string; fullUsers: number; fieldUsers: number; requestId: string }, signal?: AbortSignal): Promise<SoftwareLicencePreview> {
    return parseSoftwarePreview(await request('/licences/preview', input, signal));
  },
  async confirm(input: { workspaceId: string; previewId: string; requestId: string }, signal?: AbortSignal): Promise<{ summary: SoftwareBillingSummary | null; paymentUrl?: string; pending: boolean }> {
    return parseSoftwareConfirm(await request('/licences/confirm', input, signal));
  },
};
